/* ============================================================================
   EXCENTRED® MOTION SYSTEM — Engine
   ----------------------------------------------------------------------------
   Verantwortlich für:
   1. Scroll-Reveals (IntersectionObserver, one-shot, Stagger pro Container)
   2. Nachgeladene Inhalte (Infinite Scroll, Filter-Morph, Section-Rendering)
   3. Lazy-Image Fade-Ins
   4. Cart-Bubble Bump bei Mengenänderung
   5. Shopify Theme-Editor Events (Section-Reload)

   Flash-freie Initialisierung (wichtig!):
   Die CSS-Versteck-Regeln greifen erst unter html.ex-motion. Reihenfolge:
     a) Ziele taggen (.ex-reveal) + Stagger-Delays vergeben
     b) Alles, was bereits im/über dem Viewport liegt, SOFORT .ex-in geben
     c) ERST DANN html.ex-motion setzen
   → Es existiert kein einziger Frame, in dem sichtbarer Inhalt versteckt ist.
   Fehler vor Schritt (c) lassen die Seite komplett unangetastet.
   ========================================================================== */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Konfiguration                                                       */
  /* ------------------------------------------------------------------ */

  var REVEAL_SELECTORS = [
    '.resource-list__item',            // Produkt-/Collection-Karten (Home, Listen, Bento, Carousel, Recommendations)
    '.product-grid__item',             // Collection-/Search-Grid (main-collection)
    '.section-resource-list__header',  // Listen-Header ("FULL FIT DEALS" + SHOW ALL)
    '.trustpilot-banner--mobile',      // Trustpilot-Banner (Custom Liquid)
    '.trustpilot-banner--desktop'
  ];
  var REVEAL_SELECTOR = REVEAL_SELECTORS.join(',');

  var STAGGER_STEP_MS = 65;   // muss zu --ex-stagger-step in der CSS passen
  var STAGGER_MAX_INDEX = 8;  // Delay-Kappe, damit späte Items nicht "nachhinken"
  var IO_ROOT_MARGIN = '0px 0px -6% 0px';
  var IO_THRESHOLD = 0.05;

  var docEl = document.documentElement;
  /* currentScript ist nur während der synchronen Ausführung gültig —
     hier oben cachen, damit init() auch aus DOMContentLoaded heraus
     das data-kenburns-Attribut lesen kann. */
  var scriptEl = document.currentScript;
  var reducedMotion = false;

  try {
    reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) { /* alte Browser: Motion an lassen */ }

  /* Ohne IntersectionObserver (sehr alte Browser): sauber aussteigen —
     Seite bleibt vollständig normal, da html.ex-motion nie gesetzt wird. */
  if (reducedMotion || typeof IntersectionObserver === 'undefined') {
    return;
  }

  var observer = null;

  /* ------------------------------------------------------------------ */
  /* Reveal: Taggen + Stagger                                            */
  /* ------------------------------------------------------------------ */

  /**
   * Findet ungetaggte Ziele unterhalb von root, vergibt .ex-reveal und
   * berechnet Stagger-Delays pro Eltern-Container (nicht global — jede
   * Reihe/Sektion startet ihre eigene kleine Choreografie).
   * @returns {Element[]} die in diesem Durchlauf neu getaggten Elemente
   */
  function tagTargets(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var found = scope.querySelectorAll(REVEAL_SELECTOR);
    var fresh = [];
    var perParent = new Map();

    for (var i = 0; i < found.length; i++) {
      var el = found[i];
      if (el.classList.contains('ex-reveal')) continue;

      el.classList.add('ex-reveal');

      var parent = el.parentElement || document.body;
      var idx = perParent.get(parent) || 0;
      perParent.set(parent, idx + 1);

      var delay = Math.min(idx, STAGGER_MAX_INDEX) * STAGGER_STEP_MS;
      if (delay > 0) el.style.setProperty('--ex-d', delay + 'ms');

      fresh.push(el);
    }

    /* Falls root selbst ein Ziel ist (MutationObserver liefert Einzelnodes) */
    if (
      root && root.nodeType === 1 && root.matches &&
      root.matches(REVEAL_SELECTOR) && !root.classList.contains('ex-reveal')
    ) {
      root.classList.add('ex-reveal');
      fresh.push(root);
    }

    return fresh;
  }

  /** Übergibt Elemente an den Observer (oder revealed sofort als Fallback). */
  function observeAll(elements) {
    for (var i = 0; i < elements.length; i++) {
      if (observer) observer.observe(elements[i]);
      else elements[i].classList.add('ex-in');
    }
  }

  function onIntersect(entries) {
    for (var i = 0; i < entries.length; i++) {
      if (entries[i].isIntersecting) {
        entries[i].target.classList.add('ex-in');
        observer.unobserve(entries[i].target);
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* Lazy Images: Fade-In beim tatsächlichen Laden                       */
  /* ------------------------------------------------------------------ */

  function markLoaded() { this.classList.add('ex-img-in'); }

  function wireLazyImages(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var imgs = scope.querySelectorAll('img[loading="lazy"]:not(.ex-img-wired)');

    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      img.classList.add('ex-img-wired');

      /* Bereits geladen/gecacht → synchron sichtbar markieren,
         damit die CSS-Regel sie nie verstecken kann. */
      if (img.complete && img.naturalWidth > 0) {
        img.classList.add('ex-img-in');
      } else {
        img.addEventListener('load', markLoaded, { once: true });
        img.addEventListener('error', markLoaded, { once: true }); // kaputte Bilder nicht verstecken
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* Cart-Bubble Bump                                                    */
  /* ------------------------------------------------------------------ */

  function attachCartBump() {
    var counters = document.querySelectorAll('[ref="cartBubbleCount"]');

    function bump(counter) {
      var bubble = counter.closest ? counter.closest('.cart-bubble') : null;
      if (!bubble) return;
      bubble.classList.remove('ex-bump');
      void bubble.offsetWidth; // Reflow → Animation kann erneut starten
      bubble.classList.add('ex-bump');
    }

    for (var i = 0; i < counters.length; i++) {
      (function (counter) {
        var last = (counter.textContent || '').trim();
        var mo = new MutationObserver(function () {
          var now = (counter.textContent || '').trim();
          if (now !== last) {
            last = now;
            if (now !== '') bump(counter);
          }
        });
        mo.observe(counter, { childList: true, characterData: true, subtree: true });
      })(counters[i]);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Scan: kompletter Durchlauf für einen Teilbaum                       */
  /* ------------------------------------------------------------------ */

  function scan(root) {
    observeAll(tagTargets(root));
    wireLazyImages(root);
  }

  /* ------------------------------------------------------------------ */
  /* Initialisierung (flash-frei, siehe Kopfkommentar)                   */
  /* ------------------------------------------------------------------ */

  function init() {
    try {
      /* (a) taggen */
      var initial = tagTargets(document);
      wireLazyImages(document);

      /* (b) alles im oder über dem Viewport sofort sichtbar markieren */
      /* Runde 37 (Ladezeit): erst ALLE Positionen lesen, dann schreiben.
         Vorher wechselten Lesen (getBoundingClientRect) und Schreiben (classList.add)
         sich ab → pro Element eine erzwungene Stilberechnung der ganzen Seite. */
      var vh = window.innerHeight || docEl.clientHeight;
      var toObserve = [];
      var above = [];
      for (var i = 0; i < initial.length; i++) {
        above.push(initial[i].getBoundingClientRect().top < vh);
      }
      for (var j = 0; j < initial.length; j++) {
        if (above[j]) initial[j].classList.add('ex-in');
        else toObserve.push(initial[j]);
      }

      /* (c) Gate öffnen — ab hier greifen die Versteck-Regeln,
         aber nur noch für Elemente unterhalb des Viewports. */
      docEl.classList.add('ex-motion');

      if (scriptEl && scriptEl.getAttribute('data-kenburns') === 'true') {
        docEl.classList.add('ex-kenburns');
      }

      observer = new IntersectionObserver(onIntersect, {
        rootMargin: IO_ROOT_MARGIN,
        threshold: IO_THRESHOLD
      });
      observeAll(toObserve);

      attachCartBump();

      /* Nachgeladene Inhalte: Infinite Scroll, Facetten-Filter (Morph),
         Quick-Add-Ergebnisse etc. */
      var domWatcher = new MutationObserver(function (mutations) {
        for (var m = 0; m < mutations.length; m++) {
          var added = mutations[m].addedNodes;
          for (var n = 0; n < added.length; n++) {
            if (added[n].nodeType === 1) scan(added[n]);
          }
        }
      });
      domWatcher.observe(document.body, { childList: true, subtree: true });

      /* Shopify Theme-Editor: Sektionen werden live neu gerendert */
      document.addEventListener('shopify:section:load', function (e) {
        scan(e.target);
      });
    } catch (err) {
      /* Fail-safe: Gate öffnen war evtl. schon passiert → alles zeigen */
      try {
        var hidden = document.querySelectorAll('.ex-reveal:not(.ex-in)');
        for (var k = 0; k < hidden.length; k++) hidden[k].classList.add('ex-in');
      } catch (e2) { /* ignore */ }
      if (window.console && console.warn) console.warn('[EX Motion] disabled:', err);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
